/**
 * The attachment Home is holding for the Ask page.
 *
 * ## Why a module, and not the URL
 *
 * Home's composer does not ask; it hands over. The reader types on Home,
 * Home navigates to `?page=ask&ask=<question>`, and the Ask page is what
 * actually posts — one call, one billed answer, on the screen built to show
 * the citations. That handoff carries the question as a query parameter,
 * which works because a question is a string.
 *
 * A file is not. It cannot go in the URL, and putting it in `sessionStorage`
 * would mean multi-megabyte base64 in a 5 MB quota that the offline shell and
 * the greeting already share — one attachment could evict them.
 *
 * So it is held here, in memory, for the length of the navigation. That has a
 * real consequence and it is the right one: **a reload loses it.** An
 * attachment belongs to one question in one moment, and silently re-attaching
 * a file to a question asked twenty minutes later would be worse than asking
 * the reader to pick it again.
 *
 * `take` is deliberately destructive. The Ask page consumes the attachment
 * exactly once; leaving it here would attach the same photo to the reader's
 * next, unrelated question.
 */

import type { AskAttachment } from "./askAttachment";

let pending: AskAttachment | null = null;

/** Home puts the picked file here immediately before it navigates. */
export function holdAskAttachment(attachment: AskAttachment | null): void {
  pending = attachment;
}

/** The Ask page takes it, once. Returns `null` when there is nothing waiting
 *  — which is every navigation that did not come from an attach. */
export function takeAskAttachment(): AskAttachment | null {
  const held = pending;
  pending = null;
  return held;
}

/** What is waiting, without consuming it — for a composer that wants to draw
 *  the chip it is about to hand over. */
export function peekAskAttachment(): AskAttachment | null {
  return pending;
}
